import { useEffect, useRef, useState } from "react";
import { Link } from "react-router";
import { api } from "../../data";
import { Coco } from "../../components/States";
import { CaptchaFeld, captchaConfig } from "./Captcha";
import { fehlerText } from "../../lib/fehler";
import { t, currentLang, mitSprache } from "../../lib/sprache";

/**
 * Nach der Registrierung (Inhaber 06.10.2026): Die ganze Seite springt um auf die Code-Eingabe. Die Mail bringt einen
 * sechsstelligen Code (auch im Betreff); wer ihn eintippt, ist angemeldet und geht ohne Tabwechsel direkt zur Zahlung
 * (onVerified). Der Knopf in der Mail bleibt als Ausweichweg. Hilfe, wenn keine Mail kommt: Spam-Ordner, „erneut senden“ (mit
 * Sicherheitsprüfung, höchstens einmal je Minute) und eine andere Adresse.
 */
export function PostfachPruefen({ email, redirectTo, onBack, onVerified, ziel = "zahlung" }: {
  email: string; redirectTo: string; onBack: () => void; onVerified?: () => void;
  /** Wohin es nach dem Code geht: Zahlung (Registrierung), Einladung annehmen oder angemeldet weiter (Anmeldeseite) */
  ziel?: "zahlung" | "einladung" | "anmeldung";
}) {
  const [captcha, setCaptcha] = useState<string | null>(null);
  const [captchaRunde, setCaptchaRunde] = useState(0);
  const mitCaptcha = api.mode !== "demo" && !!captchaConfig();
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [warten, setWarten] = useState(60);
  const [code, setCode] = useState("");
  const [pruefe, setPruefe] = useState(false);
  const [codeFehler, setCodeFehler] = useState<string | null>(null);
  const eingabe = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (warten <= 0) return;
    const id = window.setTimeout(() => setWarten((s) => s - 1), 1000);
    return () => window.clearTimeout(id);
  }, [warten]);
  useEffect(() => {
    // Ganze Seite: oben beginnen, damit niemand im alten Formular stehen bleibt
    window.scrollTo({ top: 0 });
    eingabe.current?.focus();
  }, []);

  const ziffern = code.replace(/\D+/g, "");
  async function bestaetigen(wert: string) {
    if (pruefe) return;
    setPruefe(true);
    setCodeFehler(null);
    try {
      await api.verifySignupCode(email, wert);
      onVerified?.();
    } catch {
      setCodeFehler(t("Der Code stimmt nicht oder ist abgelaufen. Bitte prüfe ihn oder lass dir unten einen neuen schicken.", "The code is not correct or has expired. Please check it or have a new one sent below."));
      setPruefe(false);
    }
  }
  function aendern(v: string) {
    const nur = v.replace(/\D+/g, "").slice(0, 6);
    setCode(nur);
    setCodeFehler(null);
    // Sechs Ziffern (auch eingefügt): gleich prüfen
    if (nur.length === 6) void bestaetigen(nur);
  }

  async function erneut() {
    if (mitCaptcha && !captcha) { setFehler(t("Bitte zuerst die Sicherheitsprüfung lösen.", "Please complete the security check first.")); return; }
    setBusy(true);
    setFehler(null);
    setStatus(null);
    try {
      await api.resendSignup(email, { redirectTo, captchaToken: captcha });
      setStatus(t("Wir haben dir einen neuen Code geschickt.", "We have sent you a new code."));
      setWarten(60);
      setCode("");
      eingabe.current?.focus();
    } catch (e) {
      setFehler(fehlerText(e));
    } finally {
      setBusy(false);
      if (mitCaptcha) { setCaptcha(null); setCaptchaRunde((n) => n + 1); }
    }
  }

  return (
    <section className="panel stack tk-postfach" aria-labelledby="postfach-h">
      <div className="tk-postfach-kopf">
        <Coco pose="hinweis" size={112} />
        <div className="stack">
          <p className="eyebrow">{t("Fast geschafft", "Almost done")}</p>
          <h1 id="postfach-h">{t("Gib deinen Code ein", "Enter your code")}</h1>
          <p>{t("Wir haben dir einen sechsstelligen Code an ", "We have sent a six-digit code to ")}<strong>{email}</strong>{ziel === "zahlung" ? t(" geschickt. Danach geht es direkt weiter zur Zahlung.", ". Next you go straight on to payment.") : ziel === "anmeldung" ? t(" geschickt. Danach bist du angemeldet und machst dort weiter, wo du aufgehört hast.", ". After that you are signed in and continue where you left off.") : t(" geschickt. Danach nimmst du die Einladung an.", ". Then you accept the invitation.")}</p>
        </div>
      </div>
      <form className="stack tk-postfach-code" onSubmit={(e) => { e.preventDefault(); if (ziffern.length === 6) void bestaetigen(ziffern); }} aria-busy={pruefe}>
        <label>
          {t("Code aus der E-Mail", "Code from the email")}
          <input ref={eingabe} className="tk-faktor-code" inputMode="numeric" autoComplete="one-time-code" placeholder="123456" maxLength={12}
            value={code} onChange={(e) => aendern(e.target.value)} disabled={pruefe}
            aria-invalid={codeFehler ? true : undefined} aria-describedby={codeFehler ? "code-fehler" : "code-hinweis"} />
          <small id="code-hinweis">{t("Absender post@creatoroffice.de, der Code steht auch im Betreff.", "Sender post@creatoroffice.de; the code is also in the subject line.")}</small>
        </label>
        {codeFehler && <p id="code-fehler" className="tk-error" role="alert">{codeFehler}</p>}
        <button className="primary" type="submit" disabled={pruefe || ziffern.length !== 6}>
          {pruefe ? t("Wird geprüft …", "Checking …") : ziel === "zahlung" ? t("Bestätigen und weiter zur Zahlung", "Confirm and continue to payment") : ziel === "anmeldung" ? t("Bestätigen und anmelden", "Confirm and sign in") : t("Bestätigen", "Confirm")}
        </button>
      </form>
      <div className="stack tk-postfach-hilfe">
        <h2>{t("Keine E-Mail da?", "No email?")}</h2>
        <p>{t("Schau auch im Spam- oder Werbung-Ordner nach; die Mail kommt meist innerhalb einer Minute. Stimmt die Adresse oben nicht, nimm eine andere.", "Also check your spam or promotions folder; the email usually arrives within a minute. If the address above is wrong, use a different one.")}</p>
        {mitCaptcha && <CaptchaFeld onToken={setCaptcha} resetKey={captchaRunde} />}
        {status && <p className="notice" role="status">{status}</p>}
        {fehler && <p className="tk-error" role="alert">{fehler}</p>}
        <div className="row">
          <button type="button" disabled={busy || warten > 0} onClick={() => void erneut()}>
            {busy ? t("Wird gesendet …", "Sending …") : warten > 0 ? t(`Neuen Code senden (in ${warten} s)`, `Send new code (in ${warten} s)`) : t("Neuen Code senden", "Send new code")}
          </button>
          <button type="button" onClick={onBack}>{t("Andere E-Mail-Adresse verwenden", "Use a different email address")}</button>
        </div>
      </div>
    </section>
  );
}

/**
 * Registrierung mit einer schon registrierten Adresse (Inhaber 06.10.2026): Supabase schickt dann keine Mail. Statt auf eine
 * Bestätigung warten zu lassen, springt die Seite genauso um und führt zum Anmelden oder zum Zurücksetzen des Passworts.
 */
export function KontoVorhanden({ email, onBack }: { email: string; onBack: () => void }) {
  const lang = currentLang();
  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, []);
  return (
    <section className="panel stack tk-postfach" aria-labelledby="vorhanden-h" role="status">
      <div className="tk-postfach-kopf">
        <Coco pose="erklaert" size={112} />
        <div className="stack">
          <p className="eyebrow">{t("Schön, dass du wieder da bist", "Good to see you again")}</p>
          <h1 id="vorhanden-h">{t("Du hast schon ein Konto", "You already have an account")}</h1>
          <p>{t("Mit ", "There is already an account with ")}<strong>{email}</strong>{t(" gibt es bereits ein Konto. Darum schicken wir keine Bestätigungsmail. Melde dich einfach an, oder setz dein Passwort zurück, falls du es nicht mehr weißt.", ". That is why we do not send a confirmation email. Just sign in, or reset your password if you no longer know it.")}</p>
        </div>
      </div>
      <div className="row">
        <Link className="btn primary" to={mitSprache("/anmelden", lang)} state={{ email }}>{t("Jetzt anmelden", "Sign in now")}</Link>
        <Link className="btn" to={mitSprache("/passwort", lang)} state={{ email }}>{t("Passwort zurücksetzen", "Reset password")}</Link>
      </div>
      <div className="tk-postfach-hilfe stack">
        <p>{t("Du möchtest ein zweites, getrenntes Konto? Dann nimm eine andere E-Mail-Adresse.", "Want a second, separate account? Then use a different email address.")}</p>
        <div className="row">
          <button type="button" onClick={onBack}>{t("Andere E-Mail-Adresse verwenden", "Use a different email address")}</button>
        </div>
      </div>
    </section>
  );
}
